import React, { useState, useEffect, useMemo, Fragment } from "react";
import api from "../api/axios";

import { NavLink } from "react-router-dom";
import {
  CircularProgress,
  Grid,
  TextField,
  Autocomplete,
  Fade,
} from "@mui/material/";
import ReactPaginate from "react-paginate";
import Table from "../Components/Common/Table/Table";
import { CommonSubHeader } from "../Components/helpers/Helpers";
import TableActions from "../Components/Common/Table/TableActions";
import LoadingPage from "../Components/Common/Loading/LoadingPage";
import { Book } from "@mui/icons-material";

function Biblioteca() {
  const [objPerPage, setObjPerPage] = useState(10);
  const [pagePressed, setPagePressed] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [library, setLibrary] = useState([]);
  const [totalPagesNumber, setTotalPagesNumber] = useState(0);
  const [flag, setFlag] = useState(false);
  const [changeDone, setChangeDone] = useState(false);

  // Filtros de búsqueda
  const [queryFilters, setQueryFilters] = useState({
    title: "",
    tag: "",
    description: "",
    objPerPage: objPerPage,
  });

  const [categoryFilter, setCategoryFilter] = useState(0);
  const [openOptionsCategories, setOpenOptionsCategories] = useState(false);
  const [optionsCategories, setOptionsCategories] = useState([]);
  const loadingOptionsCategories =
    openOptionsCategories && optionsCategories.length === 0;

  // Obtener los datos de la biblioteca
  const getDataLibrary = async () => {
    setFlag(true);
    try {
      const { title, tag, description } = queryFilters;

      const parametros = {
        page: pagePressed,
        objPerPage: objPerPage,
        title,
        category: categoryFilter,
        tags: tag,
        description,
      };

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/library`,
        parametros,
      );

      const { library: responseApi, categories } = data;
      setLibrary(responseApi.data);
      setPageCount(responseApi.last_page);
      setTotalPagesNumber(responseApi.total);
      setOptionsCategories([{ category: "Seleccionar" }, ...categories]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setFlag(false);
    }
  };

  // Función para renderizar celdas con un valor por defecto
  const renderCellWithDefault = (value, defaultValue = "Sin asignar") => (
    <div
      className="spanClassForTables centerContent"
      style={{ padding: "5px" }}>
      {value || defaultValue}
    </div>
  );

  // Columnas de la tabla
  const columns = useMemo(
    () => [
      {
        Header: "Biblioteca de Archivos",
        columns: [
          {
            Header: "Titulo",
            accessor: "title",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Categoría",
            accessor: "category",
            width: 80,
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Etiquetas",
            accessor: "tags",
            width: 300,
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Descripción",
            accessor: "description",
            width: 300,
            Cell: ({ value }) =>
              renderCellWithDefault(value.replace(/<br\s*\/?>/gi, "\n")),
          },
          {
            Header: "Acciones",
            accessor: "id",
            width: 50,
            Cell: ({ value }) => (
              <TableActions
                rute={"biblioteca"}
                value={value}
                showView
                showEdit
                showDelete
                apiRute={"library"}
                module="library"
                setChangeDone={setChangeDone}
                changeDone={changeDone}
              />
            ),
          },
        ],
      },
    ],
    [],
  );

  // Manejadores de cambio en filtros
  const handleFilterChange = (field) => (event) => {
    setQueryFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCategoryChange = (_, value) => {
    setCategoryFilter(value ? value.category : 0);
  };

  const handlePageClick = ({ selected }) => setPagePressed(selected + 1);

  // Efectos
  useEffect(() => {
    const timeoutId = setTimeout(getDataLibrary, 500); // debounce
    return () => clearTimeout(timeoutId); // cleanup
  }, [queryFilters, pagePressed, categoryFilter, objPerPage, changeDone]);

  useEffect(() => {
    if (!loadingOptionsCategories) return;
    setTimeout(() => setOptionsCategories((prev) => [...prev]), 200);
  }, [loadingOptionsCategories]);

  useEffect(() => {
    if (pagePressed > pageCount) {
      setPagePressed(1);
    }
  }, [pageCount]);

  // Estructura de campos para mapeo
  const fields = useMemo(
    () => [
      {
        type: "text",
        label: "Titulo",
        value: queryFilters.title,
        onChange: handleFilterChange("title"),
      },
      {
        type: "autocomplete",
        label: "Categorías",
        value: categoryFilter,
        open: openOptionsCategories,
        onOpen: () => setOpenOptionsCategories(true),
        onClose: () => setOpenOptionsCategories(false),
        onChange: handleCategoryChange,
        options: optionsCategories,
        loading: loadingOptionsCategories,
      },
      {
        type: "text",
        label: "Etiqueta",
        value: queryFilters.tag,
        onChange: handleFilterChange("tag"),
      },
      {
        type: "text",
        label: "Descripción",
        value: queryFilters.description,
        onChange: handleFilterChange("description"),
      },
      {
        type: "select",
        label: "Nº Registros",
        value: objPerPage,
        onChange: (e) => setObjPerPage(Number(e.target.value)),
        options: [10, 20, 30, 100, 200],
      },
    ],
    [
      queryFilters,
      categoryFilter,
      openOptionsCategories,
      optionsCategories,
      loadingOptionsCategories,
      objPerPage,
    ],
  );

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Biblioteca" />

      <div className="mainContainer">
        <div className="subMainContainer">
          <Grid
            container
            direction="row"
            m={"auto"}
            item
            xs={12}
            justifyContent={"center"}
            className="filtersContainer">
            {fields.map((field, index) => (
              <Grid item xs={4} md={2} key={index} sx={{ padding: "5px" }}>
                {field.type === "text" && (
                  <TextField
                    label={field.label}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
                {field.type === "autocomplete" && (
                  <Autocomplete
                    size="small"
                    open={field.open}
                    onOpen={field.onOpen}
                    onClose={field.onClose}
                    onChange={field.onChange}
                    getOptionLabel={(option) => option.category}
                    options={field.options}
                    loading={field.loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <Fragment>
                              {field.loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                )}
                {field.type === "select" && (
                  <TextField
                    select
                    label={field.label}
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                    size="small"
                    SelectProps={{ native: true }}>
                    {field.options.map((optionValue) => (
                      <option key={optionValue} value={optionValue}>
                        {optionValue}
                      </option>
                    ))}
                  </TextField>
                )}
              </Grid>
            ))}
          </Grid>

          {flag ? (
            <LoadingPage isLoading={true} heightProp={"600px"} />
          ) : (
            <Fade in={library?.length > 0} timeout={600}>
              <div>
                <Table
                  columns={columns}
                  data={library}
                  view={"libro"}
                  viewTitle={"biblioteca"}
                  totalPagesNumber={totalPagesNumber}
                  icon={Book}
                />

                {(library != "" || flag != true) && totalPagesNumber > 10 && (
                  <div className="tablePaginateContainer">
                    <div className="paginatorLeftText">
                      Mostrando {objPerPage * (pagePressed - 1) + 1} a{" "}
                      {Math.min(objPerPage * pagePressed, totalPagesNumber)} de{" "}
                      {totalPagesNumber} resultados
                    </div>
                    <ReactPaginate
                      breakLabel="..."
                      nextLabel="Siguiente 🢂"
                      onPageChange={handlePageClick}
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={2}
                      pageCount={pageCount}
                      previousLabel="🢀 Anterior"
                      containerClassName="pagination"
                      activeClassName="active"
                      pageClassName="page-item"
                      nextClassName="page-item"
                      previousClassName="page-item"
                      disabledClassName="disabled"
                      forcePage={pagePressed - 1}
                    />
                  </div>
                )}
              </div>
            </Fade>
          )}
        </div>
      </div>
    </div>
  );
}

export default Biblioteca;
